import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  MessageCircle,
  X,
  ChevronDown,
  ChevronUp,
  Send,
  Sparkles,
  Check,
  UserPlus,
  Heart,
  PartyPopper
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * GuestEngagementWidget - Collapsible chat-style widget for guest engagement
 * Shows voluntary questions to help guests engage with the community
 * Features return visitor recognition with personalized messages
 * BEHAVIOR: Starts as collapsed icon. Only expands when user clicks it.
 * Once dismissed or completed, stays collapsed until user reopens.
 */
const GuestEngagementWidget = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [guestId, setGuestId] = useState(null);
  const [guestProfile, setGuestProfile] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [textInput, setTextInput] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [thankYouShown, setThankYouShown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isReturningVisitor, setIsReturningVisitor] = useState(false);
  const [visitCount, setVisitCount] = useState(1);
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Initialize guest tracking
  useEffect(() => {
    const initGuest = async () => {
      // Check if user has previously interacted (dismissed/completed)
      const hasSeenWidget = localStorage.getItem('pawsitively_widget_seen');
      if (hasSeenWidget) {
        setHasInteracted(true);
      }
      
      // Check for existing guest ID in localStorage
      let existingGuestId = localStorage.getItem('pawsitively_guest_id');
      
      try {
        const response = await fetch(`${API}/api/guest/track-visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ guest_id: existingGuestId })
        });
        
        if (response.ok) {
          const data = await response.json();
          setGuestId(data.guest_id);
          setVisitCount(data.visit_count || 1);
          setIsReturningVisitor(data.returning);
          localStorage.setItem('pawsitively_guest_id', data.guest_id);
          
          // Fetch guest profile for returning visitors
          if (data.returning && data.guest_id) {
            fetchGuestProfile(data.guest_id);
            setShowWelcomeBack(true);
          }
          
          // Widget stays collapsed - user must click to open
          // No auto-open behavior anymore
        }
      } catch (error) {
        console.error('Error initializing guest:', error);
      }
    };

    initGuest();
  }, []);

  // Fetch guest profile for returning visitors
  const fetchGuestProfile = async (gid) => {
    try {
      const response = await fetch(`${API}/api/guest/profile/${gid}`);
      if (response.ok) {
        const data = await response.json();
        setGuestProfile(data);
        setAnswers(data.answers || {});
      }
    } catch (error) {
      console.error('Error fetching guest profile:', error);
    }
  };

  // Fetch questions when guest ID is set
  useEffect(() => {
    if (guestId && !showWelcomeBack) {
      fetchQuestions();
    }
  }, [guestId, showWelcomeBack]);

  const fetchQuestions = async () => {
    if (!guestId) return;
    
    try {
      const response = await fetch(`${API}/api/guest/questions?guest_id=${guestId}&count=3`);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions);
        setHasMore(data.has_more);
        setCurrentQuestionIndex(0);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const submitAnswer = async (questionId, answer) => {
    if (!guestId) return;
    
    setLoading(true);
    try {
      await fetch(`${API}/api/guest/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          guest_id: guestId,
          question_id: questionId,
          answer: Array.isArray(answer) ? answer.join(', ') : answer
        })
      });
      
      // Save locally
      setAnswers(prev => ({ ...prev, [questionId]: answer }));
      
      // Move to next question
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(prev => prev + 1);
      } else if (hasMore) {
        // Fetch more questions
        await fetchQuestions();
      } else {
        // All done - mark as interacted so widget stays collapsed next time
        setThankYouShown(true);
        localStorage.setItem('pawsitively_widget_seen', 'true');
        setHasInteracted(true);
      }
      
      // Reset inputs
      setTextInput('');
      setSelectedOptions([]);
    } catch (error) {
      console.error('Error submitting answer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionSelect = (option, isMulti) => {
    if (isMulti) {
      setSelectedOptions(prev => 
        prev.includes(option) 
          ? prev.filter(o => o !== option)
          : [...prev, option]
      );
    } else {
      // Single choice - submit immediately
      const currentQuestion = questions[currentQuestionIndex];
      submitAnswer(currentQuestion.id, option);
    }
  };

  const handleMultiSubmit = () => {
    if (selectedOptions.length === 0) return;
    const currentQuestion = questions[currentQuestionIndex];
    submitAnswer(currentQuestion.id, selectedOptions);
  };

  const handleTextSubmit = () => {
    if (!textInput.trim()) return;
    const currentQuestion = questions[currentQuestionIndex];
    submitAnswer(currentQuestion.id, textInput.trim());
  };

  const skipQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (hasMore) {
      fetchQuestions();
    } else {
      setThankYouShown(true);
    }
    setTextInput('');
    setSelectedOptions([]);
  };

  const currentQuestion = questions[currentQuestionIndex];

  // Generate personalized message for returning visitors
  // Returns an object with text and optional link
  const getPersonalizedMessage = () => {
    if (!guestProfile?.answers) return { text: null, link: null };
    
    const answers = guestProfile.answers;
    if (answers.has_pets === "Yes, I have pets") {
      const petTypes = answers.pet_types;
      if (petTypes) {
        return { 
          text: `Looking for ${petTypes.toLowerCase()} communities?`,
          link: '/clubs'
        };
      }
      return { 
        text: "Looking for pet communities near you?",
        link: '/clubs'
      };
    }
    if (answers.has_pets === "No, but I'm thinking about it") {
      return { text: "Still thinking about getting a pet?", link: null };
    }
    if (answers.dream_pet) {
      return { text: `Still dreaming of that ${answers.dream_pet}?`, link: null };
    }
    if (answers.club_benefits) {
      return { 
        text: "Ready to connect with fellow pet lovers?",
        link: '/clubs'
      };
    }
    return { text: "Welcome back! We've missed you.", link: null };
  };

  // Don't render if no questions and not showing welcome back
  if (questions.length === 0 && !thankYouShown && !showWelcomeBack) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50" data-testid="guest-engagement-widget">
      {/* Collapsed state - just the toggle button */}
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="rounded-full w-14 h-14 shadow-lg bg-primary hover:bg-primary/90"
          data-testid="open-engagement-widget"
        >
          {isReturningVisitor ? (
            <Heart className="w-6 h-6" />
          ) : (
            <MessageCircle className="w-6 h-6" />
          )}
        </Button>
      )}

      {/* Expanded widget */}
      {isOpen && (
        <Card className={`w-80 shadow-2xl border-2 transition-all duration-300 ${isMinimized ? 'h-14' : ''}`}>
          {/* Header */}
          <div 
            className="flex items-center justify-between p-3 bg-primary text-primary-foreground rounded-t-lg cursor-pointer"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            <div className="flex items-center gap-2">
              {showWelcomeBack ? (
                <PartyPopper className="w-5 h-5" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              <span className="font-medium text-sm">
                {showWelcomeBack ? 'Welcome Back!' : 'Quick Question'}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 text-primary-foreground hover:bg-white/20"
                onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
              >
                {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-7 w-7 text-primary-foreground hover:bg-white/20"
                onClick={(e) => { 
                  e.stopPropagation(); 
                  setIsOpen(false);
                  // Remember that user has interacted
                  localStorage.setItem('pawsitively_widget_seen', 'true');
                  setHasInteracted(true);
                }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Content */}
          {!isMinimized && (
            <CardContent className="p-4">
              {/* Welcome back message for returning visitors */}
              {showWelcomeBack ? (
                <div className="text-center py-2">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-amber-600" />
                  </div>
                  <h4 className="font-medium mb-1">
                    {visitCount > 2 ? `Visit #${visitCount}!` : 'Nice to see you again!'}
                  </h4>
                  {(() => {
                    const message = getPersonalizedMessage();
                    return message.link ? (
                      <button
                        onClick={() => navigate(message.link)}
                        className="text-sm text-primary hover:underline mb-3 block mx-auto"
                        data-testid="communities-link"
                      >
                        {message.text}
                      </button>
                    ) : (
                      <p className="text-sm text-muted-foreground mb-3">
                        {message.text}
                      </p>
                    );
                  })()}
                  
                  {/* Registration options */}
                  <div className="space-y-2">
                    <Button 
                      size="sm" 
                      className="w-full"
                      onClick={() => navigate('/register')}
                      data-testid="join-free-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Join Free
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Create posts, add pets & more
                    </p>
                    <button 
                      onClick={() => {
                        setShowWelcomeBack(false);
                        fetchQuestions();
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Continue answering questions
                    </button>
                  </div>
                </div>
              ) : thankYouShown ? (
                // Thank you message with FREE registration option
                <div className="text-center py-4">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <h4 className="font-medium mb-1">Thanks for sharing!</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    We'd love to have you in our community.
                  </p>
                  <div className="space-y-2">
                    <Button 
                      size="sm"
                      className="w-full"
                      onClick={() => navigate('/register')}
                      data-testid="join-community-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Join Free
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Post, comment, add 1 pet & join events
                    </p>
                  </div>
                </div>
              ) : currentQuestion ? (
                // Question display
                <div className="space-y-3">
                  <p className="text-sm font-medium">{currentQuestion.question}</p>
                  
                  {/* Single choice options */}
                  {currentQuestion.question_type === 'single_choice' && (
                    <div className="space-y-2">
                      {currentQuestion.options?.map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleOptionSelect(option, false)}
                          className="w-full text-left px-3 py-2 text-sm rounded-lg border hover:bg-primary/5 hover:border-primary transition-colors"
                          disabled={loading}
                          data-testid={`option-${idx}`}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Multi choice options */}
                  {currentQuestion.question_type === 'multi_choice' && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-2">
                        {currentQuestion.options?.map((option, idx) => (
                          <Badge
                            key={idx}
                            variant={selectedOptions.includes(option) ? 'default' : 'outline'}
                            className="cursor-pointer hover:bg-primary/10"
                            onClick={() => handleOptionSelect(option, true)}
                            data-testid={`multi-option-${idx}`}
                          >
                            {option}
                          </Badge>
                        ))}
                      </div>
                      {selectedOptions.length > 0 && (
                        <Button 
                          size="sm" 
                          onClick={handleMultiSubmit}
                          disabled={loading}
                          className="w-full mt-2"
                        >
                          Continue
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Text input */}
                  {currentQuestion.question_type === 'text' && (
                    <div className="flex gap-2">
                      <Input
                        placeholder={currentQuestion.placeholder || 'Type your answer...'}
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleTextSubmit()}
                        className="flex-1 text-sm"
                        disabled={loading}
                        data-testid="text-input"
                      />
                      <Button 
                        size="icon" 
                        onClick={handleTextSubmit}
                        disabled={!textInput.trim() || loading}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  {/* Skip option */}
                  <button 
                    onClick={skipQuestion}
                    className="text-xs text-muted-foreground hover:text-foreground"
                    disabled={loading}
                  >
                    Skip this question
                  </button>
                </div>
              ) : null}

              {/* Progress indicator */}
              {!thankYouShown && questions.length > 0 && (
                <div className="flex gap-1 mt-3 justify-center">
                  {questions.map((_, idx) => (
                    <div 
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx < currentQuestionIndex ? 'bg-primary' :
                        idx === currentQuestionIndex ? 'bg-primary/50' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default GuestEngagementWidget;
